import React from "react";

import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { Box, Button, Stack, Typography } from "@mui/material";
import { IBookmark, UserLight } from "store/types";

import { PageGroup } from "./PageGroup";
import { PanelEmptyAll, PanelError, PanelLoading } from "./PanelStates";
import { SharedRow } from "./SharedRow";
import { CurrentPageContext, PanelStatus } from "./types";

export type OwnedGroup = { label: string; key: string; items: IBookmark[] };

interface Props {
  status: PanelStatus;
  ownedGroups: OwnedGroup[];
  sharedWithMe: IBookmark[];
  ownersById: Map<number, UserLight>;
  currentPage: CurrentPageContext | null;
  canSave: boolean;
  onClose: () => void;
  onRetry: () => void;
  onSaveClick: () => void;
}

export const BookmarkPanel: React.FC<Props> = ({
  status,
  ownedGroups,
  sharedWithMe,
  ownersById,
  currentPage,
  canSave,
  onClose,
  onRetry,
  onSaveClick
}) => {
  const hasAnyData = Boolean(ownedGroups.length) || Boolean(sharedWithMe.length);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        sx={{ padding: "16px 16px 12px 16px", borderBottom: "1px solid #dbe8f5" }}
      >
        <Stack direction="row" alignItems="center" gap="8px">
          <BookmarkBorderIcon sx={{ fontSize: "16px", color: "#071f35" }} />
          <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "text.primary" }}>Saved views</Typography>
        </Stack>
        {currentPage && canSave && (
          <Button
            onClick={onSaveClick}
            variant="contained"
            sx={{
              height: "28px",
              padding: "0 12px",
              borderRadius: "128px",
              fontSize: "12px",
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap"
            }}
            startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
          >
            Save view of {currentPage.pageLabel}
          </Button>
        )}
      </Stack>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {status === "loading" && <PanelLoading />}
        {status === "error" && <PanelError onRetry={onRetry} />}
        {status === "ready" && !hasAnyData && <PanelEmptyAll />}
        {status === "ready" && hasAnyData && (
          <>
            {ownedGroups.map((group) => (
              <PageGroup
                key={group.key}
                label={group.label}
                isCurrent={currentPage?.pageKey === group.key}
                bookmarks={group.items}
                onApplied={onClose}
              />
            ))}

            <Box sx={{ borderTop: ownedGroups.length ? "1px solid #dbe8f5" : "none" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ padding: "12px 16px 8px 16px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#5e7a95",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase"
                  }}
                >
                  Shared with me
                </Typography>
                {Boolean(sharedWithMe.length) && (
                  <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>{sharedWithMe.length}</Typography>
                )}
              </Stack>
              {!sharedWithMe.length ? (
                <Typography
                  sx={{
                    padding: "4px 16px 16px 16px",
                    fontSize: "13px",
                    color: "#5e7a95"
                  }}
                >
                  No views shared with you.
                </Typography>
              ) : (
                sharedWithMe.map((bookmark) => (
                  <SharedRow
                    key={bookmark.id}
                    bookmark={bookmark}
                    owner={ownersById.get(bookmark.user_id)}
                    onApplied={onClose}
                  />
                ))
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};
